import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2, Edit, Trash2, User, Mail, Phone, Star } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { ImageUpload } from "../../components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminInstructors = () => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instructorToDelete, setInstructorToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  
  const emptyForm = {
    name: "",
    email: "",
    phone: "",
    bio: "",
    specialties: "",
    image: "",
    is_active: true,
    is_default: false,
  };
  
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchInstructors();
  }, []);

  const fetchInstructors = async () => {
    try {
      const res = await axios.get(`${API}/instructors?include_inactive=true`);
      setInstructors(res.data || []);
    } catch (error) {
      console.error("Error fetching instructors:", error);
      toast.error("Failed to load instructors");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingInstructor(null);
  };

  const handleOpenDialog = (instructor = null) => {
    if (instructor) {
      setEditingInstructor(instructor);
      setFormData({
        name: instructor.name || "",
        email: instructor.email || "",
        phone: instructor.phone || "",
        bio: instructor.bio || "",
        specialties: Array.isArray(instructor.specialties) ? instructor.specialties.join(", ") : instructor.specialties || "",
        image: instructor.image || "",
        is_active: instructor.is_active !== false,
        is_default: instructor.is_default || false,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const specialtiesArray = formData.specialties
        .split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const payload = {
        ...formData,
        specialties: specialtiesArray,
      };

      if (editingInstructor) {
        await axios.put(`${API}/admin/instructors/${editingInstructor.id}`, payload);
        toast.success("Instructor updated successfully!");
      } else {
        await axios.post(`${API}/admin/instructors`, payload);
        toast.success("Instructor created successfully!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchInstructors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save instructor");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (instructor) => {
    setInstructorToDelete(instructor);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!instructorToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/instructors/${instructorToDelete.id}`);
      toast.success(`"${instructorToDelete.name}" has been deleted`);
      setDeleteDialogOpen(false);
      setInstructorToDelete(null);
      fetchInstructors();
    } catch (error) {
      toast.error("Failed to delete instructor");
    }
  };

  const handleToggleActive = (instructor) => {
    if (instructor.is_active !== false) {
      toast.warning(`Deactivate ${instructor.name}?`, {
        description: "They will no longer appear as an available instructor.",
        action: {
          label: "Deactivate",
          onClick: async () => {
            try {
              await axios.put(`${API}/admin/instructors/${instructor.id}`, { is_active: false });
              toast.success("Instructor deactivated");
              fetchInstructors();
            } catch {
              toast.error("Failed to update instructor");
            }
          },
        },
        cancel: { label: "Cancel", onClick: () => {} },
      });
      return;
    }
    axios.put(`${API}/admin/instructors/${instructor.id}`, { is_active: true })
      .then(() => { toast.success("Instructor activated"); fetchInstructors(); })
      .catch(() => toast.error("Failed to update instructor"));
  };

  const activeInstructors = instructors.filter(i => i.is_active !== false);
  const inactiveInstructors = instructors.filter(i => i.is_active === false);

  const renderInstructorCard = (instructor, index) => (
    <motion.div
      key={instructor.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className={`card-empire overflow-hidden ${instructor.is_active === false ? "opacity-60" : ""}`}>
        <div className="flex flex-col sm:flex-row">
          {/* Image */}
          <div className="relative w-full sm:w-32 h-32 sm:h-auto flex-shrink-0">
            <img
              src={instructor.image || "https://images.unsplash.com/photo-1535525153412-5a42439a210d?w=400"}
              alt={instructor.name}
              className="w-full h-full object-cover"
            />
            {instructor.is_default && (
              <Badge className="absolute top-2 left-2 bg-empire-gold text-black">
                <Star className="w-3 h-3 mr-1 fill-current" />
                Default
              </Badge>
            )}
          </div>

          {/* Content */}
          <CardContent className="flex-1 p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-bold text-white text-lg">{instructor.name}</h3>
                {instructor.is_active === false && (
                  <Badge className="bg-red-500/20 text-red-400 mt-1">Inactive</Badge>
                )}
              </div>
            </div>

            {instructor.bio && (
              <p className="text-sm text-neutral-400 mb-3 line-clamp-2">{instructor.bio}</p>
            )}

            {/* Contact Info */}
            <div className="flex flex-wrap gap-3 text-sm text-neutral-500 mb-3">
              {instructor.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {instructor.email}
                </span>
              )}
              {instructor.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {instructor.phone}
                </span>
              )}
            </div>

            {/* Specialties */}
            {instructor.specialties && instructor.specialties.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {(Array.isArray(instructor.specialties) ? instructor.specialties : [instructor.specialties]).map((spec, i) => (
                  <Badge key={i} variant="outline" className="text-xs border-neutral-700">
                    {spec}
                  </Badge>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
              <div className="flex items-center gap-2">
                <Switch
                  checked={instructor.is_active !== false}
                  onCheckedChange={() => handleToggleActive(instructor)}
                  disabled={instructor.is_default}
                />
                <span className="text-xs text-neutral-500">Active</span>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(instructor)}
                  className="text-neutral-400 hover:text-white h-8 w-8"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {!instructor.is_default && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => confirmDelete(instructor)}
                    className="text-neutral-400 hover:text-red-400 h-8 w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    </motion.div>
  );

  return (
    <div data-testid="admin-instructors">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Instructors</h1>
          <p className="text-neutral-400">Manage choreographers and instructors</p>
        </div>
        
        <Button className="btn-empire" onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Instructor
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{instructors.length}</div>
            <p className="text-sm text-neutral-400">Total Instructors</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">{activeInstructors.length}</div>
            <p className="text-sm text-neutral-400">Active</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-400">{inactiveInstructors.length}</div>
            <p className="text-sm text-neutral-400">Inactive</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-empire-gold">
              {instructors.filter(i => i.is_default).length}
            </div>
            <p className="text-sm text-neutral-400">Default</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Active/Inactive */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-neutral-900 border border-neutral-800">
          <TabsTrigger value="active" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Active ({activeInstructors.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Inactive ({inactiveInstructors.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Instructors Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {activeTab === "active" ? (
            activeInstructors.length === 0 ? (
              <Card className="card-empire col-span-full">
                <CardContent className="py-12 text-center">
                  <User className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                  <p className="text-neutral-500">No active instructors. Create your first instructor!</p>
                </CardContent>
              </Card>
            ) : (
              activeInstructors.map((instructor, index) => renderInstructorCard(instructor, index))
            )
          ) : (
            inactiveInstructors.length === 0 ? (
              <Card className="card-empire col-span-full">
                <CardContent className="py-12 text-center">
                  <User className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                  <p className="text-neutral-500">No inactive instructors</p>
                </CardContent>
              </Card>
            ) : (
              inactiveInstructors.map((instructor, index) => renderInstructorCard(instructor, index))
            )
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingInstructor ? "Edit Instructor" : "Add New Instructor"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-empire"
                placeholder="e.g., Mary Wanjiru"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-empire"
                  placeholder="mary@empiredance.co.ke"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-empire"
                  placeholder="+254 7XX XXX XXX"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="input-empire min-h-[80px]"
                placeholder="Brief biography..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Specialties (comma separated)</Label>
              <Input
                value={formData.specialties}
                onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
                className="input-empire"
                placeholder="Hip Hop, Street Dance, Afrobeat"
              />
            </div>

            <ImageUpload
              label="Profile Image"
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
              folder="instructors"
              placeholder="Upload or paste image URL"
            />

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="text-white">Active (visible when creating classes)</Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setDialogOpen(false); }}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-empire"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingInstructor ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-neutral-900 border-neutral-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Instructor</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Are you sure you want to delete "{instructorToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
